import './DeviceHistoryPage.scss';
import { Flex, Space, Spin, Typography } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { AppstoreOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../../i18n/i18n';
import EditLocationModal from '../../components/device/History/EditLocationModal';
import HistoryTable from '../../components/device/History/HistoryTable';
import { deviceApi } from '../../api/device';
import { installationApi } from '../../api/installation.api';
import { dashboardPath, devicesPath } from '../../routes';
import {
  Device,
  DeviceDriverType,
  DeviceSensorUltrasonicModel,
  DeviceType,
} from '../../gen/proto/api/gateway/v1/device_pb';
import useAuthStore from '../../stores/auth.store';
import { deviceLocationTypeLabel } from '../../utils/mapTranslationEnums';
import { DeviceTracker, DeviceTrackerType } from '../../gen/proto/api/gateway/v1/device_tracker_pb';

const { Title, Text } = Typography;
const MINUTES_PER_DAY = 86400;

function formatTimestamp(ts: { seconds: bigint; nanos: number } | undefined): string {
  if (!ts) return '-';
  return new Date(Number(ts.seconds) * 1000).toLocaleDateString('es-ES');
}

function computeDuration(inTs: { seconds: bigint } | undefined, outTs: { seconds: bigint } | undefined): string {
  const inSec = inTs ? Number(inTs.seconds) : null;
  const outSec = outTs ? Number(outTs.seconds) : Math.floor(Date.now() / 1000);
  if (inSec === null) return '-';
  const days = Math.floor((outSec - inSec) / MINUTES_PER_DAY);
  return `${days} días`;
}

type HistoryRow = {
  uuid: string;
  location: string;
  idLocation: string;
  inDate: string;
  outDate: string;
  duration: string;
};

async function buildRow(entry: DeviceTracker): Promise<HistoryRow> {
  const locationName = deviceLocationTypeLabel(entry.movementType);

  let idLocation = '-';
  if (entry.movementType === DeviceTrackerType.INSTALLATION && entry.installationUuid) {
    try {
      const installation = await installationApi.getByUuid(entry.installationUuid);
      idLocation = installation.erpId || entry.installationUuid;
    } catch {
      idLocation = entry.installationUuid;
    }
  }

  return {
    uuid: entry.uuid,
    location: locationName,
    idLocation,
    inDate: formatTimestamp(entry.inTimestamp),
    outDate: formatTimestamp(entry.outTimestamp),
    duration: computeDuration(entry.inTimestamp, entry.outTimestamp),
  };
}

const DeviceHistoryPage = () => {
  const { deviceUuid } = useParams() as { deviceUuid: string };
  const { t } = useTranslation('devices', { keyPrefix: 'history' });

  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState('-');
  const [currentLocation, setCurrentLocation] = useState('-');
  const [currentErpId, setCurrentErpId] = useState('-');
  const [currentFrom, setCurrentFrom] = useState('-');
  const [tableRows, setTableRows] = useState<HistoryRow[]>([]);
  const [device, setDevice] = useState<Device | null>(null);
  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [device, history] = await Promise.all([
        deviceApi.getDeviceByUuid(deviceUuid),
        deviceApi.getLocationHistory(deviceUuid),
      ]);
      setDevice(device);
      let deviceModel = '-';
      if (device.device.case === 'driver') {
        deviceModel = DeviceDriverType[device.device.value.model] ?? '-';
      } else if (device.device.case === 'sensor') {
        const sm = device.device.value.model;
        if (sm.case === 'ultrasonicModel') {
          deviceModel = DeviceSensorUltrasonicModel[sm.value] ?? '-';
        }
      }
      setModel(`${DeviceType[device.type]} - ${deviceModel}`);

      if (history.length > 0) {
        const current = history[0];
        setCurrentLocation(deviceLocationTypeLabel(current.movementType));
        setCurrentFrom(formatTimestamp(current.inTimestamp));

        if (current.movementType === DeviceTrackerType.INSTALLATION && current.installationUuid) {
          try {
            const installation = await installationApi.getByUuid(current.installationUuid);
            setCurrentErpId(installation.erpId || current.installationUuid);
          } catch {
            setCurrentErpId(current.installationUuid);
          }
        } else {
          setCurrentErpId(' ');
        }
      } else {
        setCurrentLocation(' ');
        setCurrentErpId(' ');
      }

      const rows = await Promise.all(history.map(buildRow));
      setTableRows(rows);
    } finally {
      setLoading(false);
    }
  }, [deviceUuid]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Flex vertical>
      <Spin spinning={loading} size={'large'} fullscreen={true} style={{ zIndex: 999 }} />
      <br />
      <Space size={'middle'}>
        <AppstoreOutlined className={'DeviceHistoryPage__icon'} />
        <Title level={1} className={'DeviceHistoryPage__title'}>
          <Link to={`/${dashboardPath}/${devicesPath}`}>{t('breadcrumb')}</Link>
          {' / '}
          <span className={'DeviceHistoryPage__title__current'}>
            {t('title')}
            {device?.name}
          </span>
        </Title>
      </Space>

      <div className={'DeviceHistoryPage__deviceInfo'}>
        <Text className={'DeviceHistoryPage__deviceInfo__deviceId'}>
          {device?.name} - {model}
        </Text>
        {isSuperAdminOrCompanyAdmin && (
          <EditLocationModal deviceName={device?.name || ''} deviceUuid={device?.uuid || ''} onSaved={fetchData} />
        )}
      </div>
      <div className={'DeviceHistoryPage__locationInfo'}>
        <div className={'DeviceHistoryPage__locationInfo__current'}>
          <Text type={'secondary'}>{t('currentLocation')}</Text>
          <Text className={'DeviceHistoryPage__locationInfo__current__text'}>
            {currentLocation} - {currentErpId}
          </Text>
        </div>
        <Text className={'DeviceHistoryPage__locationInfo__current__text'}>
          {t('from')} {currentFrom}
        </Text>
      </div>
      <HistoryTable
        items={tableRows}
        actionNode={
          isSuperAdminOrCompanyAdmin ? (
            <EditLocationModal deviceName={device?.name || ''} deviceUuid={device?.uuid || ''} onSaved={fetchData} />
          ) : undefined
        }
      />
    </Flex>
  );
};

export default DeviceHistoryPage;
